import { useMemo } from "react";
// Material UI
import { GridColDef, GridGroupingColDefOverride, GridValueGetter } from "@mui/x-data-grid-pro";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { Stack } from "@mui/material";

// Components
import { ExpandCollapseAllButtonHeader } from "@/Layouts/components/TreeGrid/ExpandCollapseAllButtonHeader";
import { CustomGridTreeDataGroupingCell } from "@/Layouts/components/TreeGrid/CustomGridTreeDataGroupingCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { Coin } from "../Coin";

// Constants
import { HEADER_CLASS_NAME } from "@/Constants/tables";
import { DATE_FORMAT } from "@/Constants/date";

// Helpers
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
import {
	augmentColumnDefinitions,
	generateRenderingFn,
	getPinnedCellClassName,
	isAutoGeneratedFooter,
	WithTooltip,
} from "@/Layouts/components/Tables/DataSmartTable/helpers/";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Hooks
import { useCitContextData } from "@/DataPages/context/cit/CitContext";

// Types
import { ICitActivity } from "@/Common/types/ContractsInTransit/model";
import { CustomGridTreeDataGroupingCellProps } from "@/Layouts/components/types";
import { getFundedStatus } from "@DataPages/helpers/financeManagement/cit/citTable";
import { NOT_APPLICABLE } from "@/DataPages/constants/cit";

interface IUseCitTableConfiguration {
	ageFooter: number;
	amountFinancedFooter: number;
	balanceFooter: number;
	dealNumberCountFooter: number;
	transactionAmountFooter: number;
	vinNumberCountFooter: number;
}

const baseConfig: Partial<GridColDef> = {
	align: "right",
	headerClassName: HEADER_CLASS_NAME,
	renderHeader: (params) => <TypographyHeader params={params} right={params.colDef.align === "right"} />,
	cellClassName: getCellClassNameFn(),
	renderCell: generateRenderingFn(),
	resizable: false,
	type: "string",
};

const ifFooterValueGetter =
	(footerValue: number | string): GridValueGetter =>
	(value, row, _column, apiRef) => {
		if (isAutoGeneratedFooter(apiRef.current.getRowId(row))) {
			return footerValue;
		}
		return value;
	};

const groupingColDef: GridGroupingColDefOverride = {
	width: 48,
	maxWidth: 48,
	minWidth: 48,
	resizable: false,
	headerName: "Group",
	renderHeader: () => (
		<Stack alignItems="center" justifyContent="center" className="relative w-7 h-9">
			<ExpandCollapseAllButtonHeader className="left-[1px]" />
		</Stack>
	),
	cellClassName: (param) => {
		return getPinnedCellClassName(param, param.rowNode.depth ? "[background:unset_!important] group-child" : "");
	},
	colSpan: (value) => (!value ? 2 : 1),
	renderCell: (params) => {
		if (isAutoGeneratedFooter(params.id)) {
			return "Total";
		}
		return (
			<div className="pt-[2px]">
				<CustomGridTreeDataGroupingCell {...(params as CustomGridTreeDataGroupingCellProps)} />
			</div>
		);
	},
	getApplyQuickFilterFn: () => null,
};

const valueGetterDate = (value: string) => value && getDateInUTC(value);
const dateFormatter = (value: string | number | Date) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded);
const renderCellDate = (params: GridRenderCellParams) => {
	if (isFooterCell(params)) {
		return <TypographyCell />;
	}
	return <TypographyCell>{dateFormatter(params?.value)}</TypographyCell>;
};

const baseColumnDefinitions: GridColDef[] = [
	{
		field: "_id",
		headerName: "",
		width: 1,
		filterable: false,
		sortable: false,
	},
	{
		headerName: "Funded",
		field: "__isFunded",
		width: 70,
		align: "center",
		cellClassName: getPinnedCellClassName,
		type: "singleSelect",
		valueOptions: ["Funded", "Unfunded", NOT_APPLICABLE],
		renderCell: ({ value }) => {
			if (value === NOT_APPLICABLE) {
				return <></>;
			}
			return (
				<TypographyCell component={"div"}>
					<Coin active={value === "Funded"} activeText="Funded" inactiveText="Unfunded" />
				</TypographyCell>
			);
		},
	},
	{
		field: "transactionDate",
		headerName: "Transaction Date",
		type: "date",
		align: "left",
		width: 100,
		valueGetter: valueGetterDate,
		valueFormatter: dateFormatter,
		cellClassName: getPinnedCellClassName,
		renderCell: renderCellDate,
	},
	{
		field: "dealNumber",
		headerName: "Deal #",
		width: 80,
		align: "right",
		cellClassName: getPinnedCellClassName,
	},
	{
		field: "balance",
		headerName: "Balance",
		type: "number",
		width: 110,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params: GridRenderCellParams) => (
			<TypographyCell params={params} render={"absolute"} hints={{ isMoney: true, noFocus: true }} />
		),
		cellClassName: getPinnedCellClassName,
	},
	{
		field: "control",
		headerName: "Control",
		width: 80,
		cellClassName: getPinnedCellClassName,
	},
	{
		field: "__empty",
		headerName: "",
		sortable: false,
		resizable: false,
		filterable: false,
		width: 12,
		headerClassName: "!z-10 !bg-white !border-none !p-0",
		cellClassName: () => "empty-row !bg-white !border-none !p-0",
		renderCell: () => <TypographyCell className={"!bg-blue"} />,
	},
	{
		field: "source",
		headerName: "Source",
		type: "number",
		width: 65,
		renderCell: generateRenderingFn({}, { customFooterValue: " " }),
	},
	{
		field: "date",
		headerName: "Date",
		type: "date",
		align: "left",
		width: 80,
		valueGetter: valueGetterDate,
		valueFormatter: dateFormatter,
		renderCell: renderCellDate,
	},
	{
		field: "account",
		headerName: "Account",
		width: 80,
	},
	{
		field: "transactionAmount",
		headerName: "Transaction Amount",
		type: "number",
		width: 115,
		renderHeader: (params) => <TypographyHeader params={params} right={false} />,
		renderCell: (params: GridRenderCellParams) => (
			<TypographyCell params={params} render={"absolute"} hints={{ isMoney: true, noFocus: true }} />
		),
	},
	{
		field: "controlType",
		headerName: "Control Type",
		width: 80,
	},
	{
		field: "stockNumber",
		headerName: "Stock #",
		width: 80,
	},
	{
		field: "customerNumber",
		headerName: "Customer #",
		width: 100,
	},

	{
		field: "fIDate",
		headerName: "F&I Date",
		type: "date",
		align: "left",
		width: 80,
		valueGetter: valueGetterDate,
		valueFormatter: dateFormatter,
		renderCell: renderCellDate,
	},
	{
		field: "customerName",
		headerName: "Customer Name",
		align: "left",
		width: 120,
		renderCell: WithTooltip,
	},
	{
		field: "vinNumber",
		align: "left",
		headerName: "VIN (8)",
		width: 120,
	},
	{
		field: "lender",
		align: "left",
		headerName: "Lender",
		width: 130,
		renderCell: WithTooltip,
	},

	{
		field: "amountFinanced",
		headerName: "Financed Amount",
		type: "number",
		width: 120,
		align: "right",
		renderHeader: (params) => <TypographyHeader params={params} right={false} />,
		renderCell: (params: GridRenderCellParams) => (
			<TypographyCell params={params} render={"absolute"} hints={{ isMoney: true, noFocus: true }} />
		),
	},
	{
		field: "financeEmployeeId",
		headerName: "Finance Employee Id",
	},
	{
		field: "financeEmployee",
		align: "left",
		headerName: "F&I Manager",
		width: 110,
		renderCell: WithTooltip,
	},
	{
		field: "deskManagerId",
		headerName: "Desk Manager Id",
		width: 10,
	},
	{
		field: "deskManager",
		align: "left",
		headerName: "Desk Manager",
		width: 120,
		renderCell: WithTooltip,
	},
	{
		field: "salesManagerEmployeeId",
		headerName: "Sales Manager Id",
		width: 10,
	},
	{
		field: "salesManager",
		align: "left",
		headerName: "Sales Manager",
		width: 120,
		renderCell: WithTooltip,
	},
	{
		field: "salesEmployeeId",
		headerName: "Salesperson Id",
		width: 10,
	},
	{
		field: "salesEmployee",
		align: "left",
		headerName: "Salesperson",
		width: 120,
		renderCell: WithTooltip,
	},
	{
		field: "tenDaySent",
		headerName: "Ten Day Sent",
		width: 100,
	},
	{
		field: "stage",
		headerName: "Stage",
		width: 150,
	},
	{
		field: "trade",
		headerName: "Trade",
		type: "string",
		width: 100,
	},
	{
		field: "description",
		align: "left",
		headerName: "Description",
		width: 150,
		renderCell: WithTooltip,
	},
	{
		field: "glDesc",
		align: "left",
		headerName: "GL-DESC",
		width: 150,
		renderCell: WithTooltip,
	},
	{
		field: "age",
		headerName: "Age",
		type: "number",
		width: 60,
	},

	{
		field: "referenceNumber",
		headerName: "Reference #",
		width: 90,
	},
	{
		field: "postingDate",
		headerName: "Posting Date",
		type: "date",
		align: "left",
		width: 80,
		valueGetter: valueGetterDate,
		valueFormatter: dateFormatter,
		renderCell: renderCellDate,
	},
	{
		field: "creationDate",
		headerName: "Creation Date",
		type: "date",
		align: "left",
		width: 80,
		renderCell: renderCellDate,
		valueGetter: valueGetterDate,
		valueFormatter: dateFormatter,
	},
];

export const useCITTableColumns = ({
	ageFooter,
	amountFinancedFooter,
	balanceFooter,
	dealNumberCountFooter,
	transactionAmountFooter,
	vinNumberCountFooter,
}: IUseCitTableConfiguration) => {
	const { isFundable, isFunded } = useCitContextData();
	const columns = useMemo<GridColDef[]>(
		() =>
			baseColumnDefinitions
				.map(
					augmentColumnDefinitions<"__isFunded" | "__empty" | keyof ICitActivity>({
						__isFunded: {
							valueGetter: (_value, activity: ICitActivity) => {
								if (isAutoGeneratedFooter(activity._id) || !isFundable(activity)) {
									return NOT_APPLICABLE;
								}
								return getFundedStatus(activity, "hierarchy");
							},
						},
						balance: {
							valueGetter: ifFooterValueGetter(balanceFooter),
						},
						transactionAmount: {
							valueGetter: ifFooterValueGetter(transactionAmountFooter),
						},
						vinNumber: {
							valueGetter: ifFooterValueGetter(vinNumberCountFooter),
						},
						amountFinanced: {
							valueGetter: ifFooterValueGetter(amountFinancedFooter),
						},
						dealNumber: {
							valueGetter: ifFooterValueGetter(dealNumberCountFooter),
						},
						age: {
							valueGetter: ifFooterValueGetter(ageFooter),
						},
					})
				)
				.map((customConfig) => ({
					...baseConfig,
					...customConfig,
					minWidth: customConfig.minWidth ?? customConfig.width,
					maxWidth: customConfig.maxWidth ?? customConfig.width,
				})),
		[isFunded, ageFooter, amountFinancedFooter, balanceFooter, dealNumberCountFooter, transactionAmountFooter, vinNumberCountFooter]
	);

	return useMemo(
		() => ({
			columns,
			groupingColDef,
		}),
		[columns]
	);
};
